import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { Search, Globe, Send, CheckCircle2, Save, Radio, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SEO panel — paste Google/Bing/Yandex verification tokens, trigger manual
 * sitemap pings, fire IndexNow submissions.
 */
export default function SeoPanel() {
  const [google, setGoogle] = useState('');
  const [bing, setBing] = useState('');
  const [yandex, setYandex] = useState('');
  const [indexNowKey, setIndexNowKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [submittingUrls, setSubmittingUrls] = useState('');

  // Load stored tokens
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/seo/indexnow-key`);
        setIndexNowKey(res.data.key);
      } catch {}

      // existing stored verification values
      ['google', 'bing', 'yandex'].forEach(async (engine) => {
        try {
          const r = await axios.get(`${API}/seo/verification/${engine}`);
          if (engine === 'google') setGoogle(r.data.content || '');
          if (engine === 'bing') setBing(r.data.content || '');
          if (engine === 'yandex') setYandex(r.data.content || '');
        } catch {
          // 404 = not yet stored. Fine.
        }
      });
    })();
  }, []);

  const saveVerification = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (google.trim()) payload.google_site_verification = google.trim();
      if (bing.trim()) payload.bing_site_verification = bing.trim();
      if (yandex.trim()) payload.yandex_verification = yandex.trim();
      if (Object.keys(payload).length === 0) {
        toast.error('Enter at least one verification code');
        return;
      }
      await axios.post(`${API}/seo/verification`, payload, { withCredentials: true });
      toast.success('Verification codes saved. They will appear as meta tags on next page load.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const pingSitemap = async () => {
    setPinging(true);
    try {
      const res = await axios.post(`${API}/seo/ping-google`, null, { withCredentials: true });
      toast.success(`Pinged: ${Object.keys(res.data.results).length} endpoints`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Ping failed');
    } finally {
      setPinging(false);
    }
  };

  const submitIndexNow = async () => {
    const urls = submittingUrls
      .split(/[\n,\s]+/)
      .map((u) => u.trim())
      .filter((u) => u.startsWith('https://'));
    if (urls.length === 0) {
      toast.error('Paste at least one https:// URL');
      return;
    }
    try {
      const res = await axios.post(
        `${API}/seo/indexnow/submit`,
        { urls },
        { withCredentials: true }
      );
      toast.success(`Queued ${res.data.queued} URLs to IndexNow providers`);
      setSubmittingUrls('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed');
    }
  };

  return (
    <Card data-testid="seo-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="w-5 h-5 text-amber-500" />
          Search Engine Visibility
        </CardTitle>
        <CardDescription>
          Paste site-verification codes once, then ping search engines whenever new content is
          published.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Site verification */}
        <section className="space-y-3">
          <h3 className="font-medium flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Site Verification Codes
          </h3>
          <Alert>
            <Info className="w-4 h-4" />
            <AlertDescription>
              Grab codes from{' '}
              <a
                href="https://search.google.com/search-console"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Google Search Console
              </a>{' '}
              and{' '}
              <a
                href="https://www.bing.com/webmasters"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Bing Webmaster Tools
              </a>
              . Choose the &quot;HTML tag&quot; / meta-tag verification method and paste the{' '}
              <code>content</code> attribute here.
            </AlertDescription>
          </Alert>
          <div className="space-y-2">
            <Label htmlFor="google-verif">Google meta content</Label>
            <Input
              id="google-verif"
              placeholder="e.g., ABCD1234xyz…"
              value={google}
              onChange={(e) => setGoogle(e.target.value)}
              data-testid="google-verif-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="bing-verif">Bing meta content</Label>
            <Input
              id="bing-verif"
              placeholder="e.g., 123ABCXYZ…"
              value={bing}
              onChange={(e) => setBing(e.target.value)}
              data-testid="bing-verif-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="yandex-verif">Yandex meta content (optional)</Label>
            <Input
              id="yandex-verif"
              placeholder="e.g., abcdef1234…"
              value={yandex}
              onChange={(e) => setYandex(e.target.value)}
              data-testid="yandex-verif-input"
            />
          </div>
          <Button onClick={saveVerification} disabled={saving} data-testid="save-verif-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Verification Codes
          </Button>
        </section>

        {/* Sitemap ping */}
        <section className="space-y-3 border-t pt-4">
          <h3 className="font-medium flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Sitemap Ping
          </h3>
          <p className="text-sm text-muted-foreground">
            Tell Google &amp; Bing to re-crawl the sitemap now. Use after big content drops.
          </p>
          <Button variant="outline" onClick={pingSitemap} disabled={pinging} data-testid="ping-sitemap-btn">
            {pinging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Radio className="w-4 h-4 mr-2" />}
            Ping Google &amp; Bing Now
          </Button>
        </section>

        {/* IndexNow */}
        <section className="space-y-3 border-t pt-4">
          <h3 className="font-medium flex items-center gap-2">
            <Send className="w-4 h-4" />
            IndexNow (Bing / Yandex / Seznam)
          </h3>
          {indexNowKey && (
            <p className="text-xs text-muted-foreground">
              Key file:{' '}
              <code className="bg-muted px-1 py-0.5 rounded">
                /api/seo/indexnow-key/{indexNowKey}.txt
              </code>
            </p>
          )}
          <div className="space-y-2">
            <Label htmlFor="indexnow-urls">URLs to submit (one per line, https:// only)</Label>
            <Textarea
              id="indexnow-urls"
              rows={4}
              placeholder={`https://americandogclub.com/blog/my-new-post\nhttps://americandogclub.com/events/upcoming`}
              value={submittingUrls}
              onChange={(e) => setSubmittingUrls(e.target.value)}
              data-testid="indexnow-urls-input"
            />
          </div>
          <Button onClick={submitIndexNow} disabled={!submittingUrls.trim()} data-testid="submit-indexnow-btn">
            <Send className="w-4 h-4 mr-2" />
            Submit to IndexNow
          </Button>
        </section>
      </CardContent>
    </Card>
  );
}
